// Helper function to format supplier category
export const formatSupplierCategory = (category) => {
  if (!category) return 'N/A';
  
  const categoryMap = {
    'manufacturing': 'Manufacturing',
    'services': 'Services',
    'trading': 'Trading',
    'rw-raw-material': 'Raw Material',
    'pk-packaging': 'Packaging',
    'rw': 'Raw Material',
    'pk': 'Packaging'
  };
  
  return categoryMap[category] || category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to format supplier type
export const formatSupplierType = (type) => {
  if (!type) return 'N/A';
  
  const typeMap = {
    'supplier': 'Supplier',
    'manufacturer': 'Manufacturer',
    'service_provider': 'Service Provider',
    'distributor': 'Distributor'
  };
  
  return typeMap[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to format MSME status
export const formatMsmeStatus = (status) => {
  if (!status) return 'N/A';
  
  const statusMap = {
    'msme': 'MSME',
    'non_msme': 'Non-MSME',
    'pending': 'Pending',
    'large': 'Large'
  };
  
  return statusMap[status] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to format MSME category
// Derives category display value from both msme_status and msme_category fields
export const formatMsmeCategory = (msmeStatus, msmeCategory) => {
  // Normalize msme_status to lowercase for comparison
  const normalizedStatus = msmeStatus ? String(msmeStatus).toLowerCase() : '';
  
  // If msme_status is NON_MSME and msme_category is empty/null, return "Non-MSME"
  if ((normalizedStatus === 'non_msme' || normalizedStatus === 'non-msme') && !msmeCategory) {
    return 'Non-MSME';
  }
  
  // If msme_category exists, return it (properly formatted)
  if (msmeCategory) {
    // Handle common category values
    const categoryMap = {
      'micro': 'Micro',
      'small': 'Small',
      'medium': 'Medium',
      'non-msme': 'Non-MSME',
      'non_msme': 'Non-MSME'
    };
    
    const normalizedCategory = String(msmeCategory).toLowerCase().trim();
    if (categoryMap[normalizedCategory]) {
      return categoryMap[normalizedCategory];
    }
    
    // Format the category: replace underscores with spaces and capitalize words
    return String(msmeCategory).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
  
  // Default fallback
  return 'N/A';
};

// Helper function to format business vertical
export const formatBusinessVertical = (vertical) => {
  if (!vertical) return 'N/A';
  
  const verticalMap = {
    'manufacturing': 'Manufacturing',
    'services': 'Services',
    'trading': 'Trading',
    'construction': 'Construction',
    'technology': 'Technology',
    'healthcare': 'Healthcare',
    'education': 'Education',
    'finance': 'Finance',
    'retail': 'Retail',
    'logistics': 'Logistics',
    'picl-enterprises': 'PICL'
  };
  
  return verticalMap[vertical] || vertical.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to format industry sector
export const formatIndustrySector = (sector) => {
  if (!sector) return 'N/A';
  
  const sectorMap = {
    'automotive': 'Automotive',
    'electronics': 'Electronics',
    'chemicals': 'Chemicals',
    'pharmaceuticals': 'Pharmaceuticals',
    'textiles': 'Textiles',
    'food_beverage': 'Food & Beverage',
    'construction': 'Construction',
    'energy': 'Energy',
    'telecommunications': 'Telecommunications',
    'it_software': 'IT & Software',
    'healthcare': 'Healthcare',
    'education': 'Education',
    'finance': 'Finance',
    'retail': 'Retail',
    'logistics': 'Logistics'
  };
  
  return sectorMap[sector] || sector.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to format supplier group
export const formatSupplierGroup = (group) => {
  if (!group) return 'N/A';
  
  const groupMap = {
    'strategic': 'Strategic',
    'preferred': 'Preferred',
    'approved': 'Approved',
    'potential': 'Potential',
    'blacklisted': 'Blacklisted',
    'oem-customer': 'OEM-Customer Referred Supplier',
    'odm-amber': 'ODM-PICLDeveloped Supplier',
    'other': 'Other'
  };
  
  return groupMap[group] || group.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to format account type
export const formatAccountType = (accountType) => {
  if (!accountType) return 'N/A';
  
  const accountTypeMap = {
    'savings': 'Savings Account',
    'current': 'Current Account',
    'od': 'Overdraft Account'
  };
  
  return accountTypeMap[accountType] || accountType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to format employee count
export const formatEmployeeCount = (count) => {
  if (!count) return 'N/A';
  
  const countMap = {
    '1_10': '1-10',
    '11_50': '11-50',
    '51_200': '51-200',
    '201_500': '201-500',
    '501_1000': '501-1000',
    '1000_plus': '1000+'
  };
  
  return countMap[count] || count.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to format annual turnover
export const formatAnnualTurnover = (turnover) => {
  if (!turnover) return 'N/A';
  
  if (typeof turnover === 'number') {
    return `₹${turnover.toLocaleString()}`;
  }
  
  const turnoverMap = {
    'less_1cr': 'Less than ₹1 Cr',
    '1_5cr': '₹1-5 Cr',
    '5_10cr': '₹5-10 Cr',
    '10_50cr': '₹10-50 Cr',
    '50_100cr': '₹50-100 Cr',
    '100cr_plus': '₹100 Cr+'
  };
  
  return turnoverMap[turnover] || turnover.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Function to capitalize first letter of every word
export const capitalizeWords = (str) => {
  if (!str) return str;
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};
